import React, { useEffect, useState } from "react";
import "./footer.css";
import { nav, profile, socials } from "../../data/site";
import Icon from "../ui/Icon";
import BrandIcon from "../ui/BrandIcon";
import FitWord from "./FitWord";

const links = socials.filter((s) => s.url);

/** Real local time in Bengaluru, ticking every second. */
const formatTime = () => {
  try {
    return new Intl.DateTimeFormat("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true, timeZone: "Asia/Kolkata" }).format(new Date());
  } catch {
    return "";
  }
};

const useLocalTime = () => {
  const [time, setTime] = useState(formatTime);
  useEffect(() => {
    const id = window.setInterval(() => setTime(formatTime()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return time;
};

const Footer = () => {
  const [copied, setCopied] = useState(false);
  const localTime = useLocalTime();

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(profile.email);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      window.location.href = `mailto:${profile.email}`;
    }
  };

  return (
    <footer className="footer" id="footer">
      {/* Looping strip: decorative and mouse-only. The same links are in the
          Find me elsewhere grid, so both copies stay out of assistive tech and the tab order. */}
      <div className="footer__ticker">
        <div className="footer__tickerTrack">
          {[0, 1].map((n) => (
            <ul key={n} aria-hidden="true">
              {links.map((s) => (
                <li key={s.id}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    tabIndex={-1}
                  >
                    <BrandIcon name={s.id} className="ticker__brand" />
                    {s.label}
                    <Icon name="external" className="icon ticker__arrow" />
                  </a>
                  <span className="ticker__sep" aria-hidden="true">✦</span>
                </li>
              ))}
            </ul>
          ))}
        </div>
      </div>

      <div className="footer__body container">
        <div className="footer__lead">
          <p className="footer__eyebrow">Colophon / Contact</p>
          <h2 className="footer__title">
            Have a problem worth <em>solving?</em>
          </h2>
          <div className="footer__cta">
            <a className="btn btn--primary" href={`https://mail.google.com/mail/u/0/?fs=1&tf=cm&to=${profile.email}`} target="_blank" rel="noreferrer">
              <Icon name="mail" />
              {profile.email}
            </a>
            <button type="button" className="btn" onClick={copyEmail}>
              <Icon name={copied ? "check" : "copy"} />
              {copied ? "Copied" : "Copy email"}
            </button>
            <span className="sr-only" role="status">
              {copied ? "Email address copied" : ""}
            </span>
          </div>
        </div>

        <nav className="footer__index" aria-label="Profiles">
          <p className="footer__eyebrow">Find me elsewhere</p>
          <ul className="footer__grid">
            {links.map((s, i) => (
              <li key={s.id} style={{ "--i": i }}>
                <a className="tile" href={s.url} target="_blank" rel="noreferrer">
                  <span className="tile__mark" aria-hidden="true">
                    <BrandIcon name={s.id} />
                  </span>
                  <span className="tile__text">
                    <span className="tile__label">{s.label}</span>
                    {s.handle && <span className="tile__handle">{s.handle}</span>}
                  </span>
                  <Icon name="external" className="icon tile__arrow" />
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <FitWord className="footer__word" text={profile.handle} />

      <div className="footer__bar container">
        <p className="footer__legal">
          <span>© {new Date().getFullYear()} {profile.name}</span>
          <span className="footer__where">
            {profile.location}
            {localTime && <span className="footer__time"> · {localTime} IST</span>}
          </span>
        </p>
        <nav aria-label="Footer">
          <ul className="footer__links">
            {nav.filter((item) => item.id !== "contact").map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.label}</a>
              </li>
            ))}
          </ul>
        </nav>
        <div className="footer__end">
          <button type="button" className="footer__top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            Back to top <Icon name="up" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
